/**
 * Query keys: Insights shell (KPI summary, portfolio breakdowns, community/portfolio directory, overview metrics, report export) plus Leasing, Delinquency, Lease Abstraction dashboards.
 *
 * Keys are arrays that start with a stable domain prefix so `invalidateQueries`
 * can target a whole domain by prefix. Put every input the response depends on
 * (account, user, portfolio ids, filters, date range) into the key.
 */

const ROOT = 'insightsCore'

/** Sorted numeric copy of an id list the server treats as a set. */
export const sortedIdsForKey = (ids = []) =>
  (Array.isArray(ids) ? ids : String(ids ?? '').split(','))
    .filter((id) => id !== '' && id !== null && id !== undefined)
    .map(Number)
    .filter(Number.isFinite)
    .sort((left, right) => left - right)

/** Sorted string copy of a filter list the server treats as a set (lead sources). */
const sortedStringsForKey = (values = []) =>
  (Array.isArray(values) ? values : String(values || '').split(','))
    .map(String)
    .filter(Boolean)
    .sort()

/**
 * Normalize a dashboard request scope for a key: sorted portfolio ids and lead
 * sources, empty values dropped so "no filter" and "undefined filter" share a key.
 */
const scopeForKey = ({ portfolioIds, leadSources, ...rest } = {}) => {
  const scope = {}
  Object.entries(rest).forEach(([name, value]) => {
    if (value === undefined || value === null || value === '') return
    scope[name] = value
  })
  if (portfolioIds !== undefined) scope.portfolioIds = sortedIdsForKey(portfolioIds)
  const sources = sortedStringsForKey(leadSources)
  if (sources.length > 0) scope.leadSources = sources
  return scope
}

export const insightsCoreKeys = {
  all: [ROOT],

  /** Community portfolio directory for the signed-in user. */
  communityPortfolios: (userId) => [ROOT, 'communityPortfolios', userId ?? null],

  /** KPI summary job lifecycle (start + poll) for one portfolio selection. */
  kpiSummary: ({ accountId, portfolioIds }) => [
    ROOT,
    'kpiSummary',
    scopeForKey({ accountId, portfolioIds }),
  ],

  /** Fallback per-portfolio KPI breakdowns (polled until the server cache is warm). */
  portfolioBreakdowns: ({ accountId, portfolioIds }) => [
    ROOT,
    'portfolioBreakdowns',
    scopeForKey({ accountId, portfolioIds }),
  ],

  /** Delinquency monthly trend shown on the overview cards. */
  delinquencyMonthlyTrend: ({ accountId, portfolioIds, months }) => [
    ROOT,
    'delinquencyMonthlyTrend',
    scopeForKey({ accountId, portfolioIds, months }),
  ],

  /** Status of one server-side report export job. */
  reportExportJob: (jobId) => [ROOT, 'reportExportJob', String(jobId)],

  leasing: {
    all: [ROOT, 'leasing'],
    /** One leasing dashboard endpoint for a request scope (portfolios, dates, lead sources). */
    dashboard: (endpoint, scope) => [ROOT, 'leasing', 'dashboard', endpoint, scopeForKey(scope)],
    /**
     * Every cached `/api/leasing/dashboard/metrics` read: the Leasing dashboard's
     * and the Escalations overview's (`realtime: true`) entries.
     */
    metricsAll: [ROOT, 'leasing', 'dashboard', 'metrics'],
    /** Leasing data availability across every portfolio the user can access. */
    availability: (authorizedPortfolioIds) => [
      ROOT,
      'leasing',
      'availability',
      sortedIdsForKey(authorizedPortfolioIds),
    ],
    /** Tour briefing or booking email for one prospect. */
    tourEmail: (emailType, prospectPeopleId) => [
      ROOT,
      'leasing',
      'tourEmail',
      emailType,
      String(prospectPeopleId),
    ],
    /** Escalation emails and replies for one thread. */
    escalationEmails: (threadId) => [ROOT, 'leasing', 'escalationEmails', String(threadId)],
  },

  delinquency: {
    all: [ROOT, 'delinquency'],
    /** One delinquency dashboard endpoint for a request scope. */
    dashboard: (endpoint, scope) => [
      ROOT,
      'delinquency',
      'dashboard',
      endpoint,
      scopeForKey(scope),
    ],
    /** Delinquency data availability for the account or selected portfolios. */
    availability: ({ accountId, portfolioIds }) => [
      ROOT,
      'delinquency',
      'availability',
      scopeForKey({ accountId, portfolioIds }),
    ],
    /** One resident drill-down section. */
    resident: (section, { personId, portfolioId }) => [
      ROOT,
      'delinquency',
      'resident',
      String(personId),
      String(portfolioId),
      section,
    ],
    /** Payment timing drill-down (reasons or AI themes) for one timing segment. */
    paymentTimingDrilldown: (kind, { category, ...scope }) => [
      ROOT,
      'delinquency',
      'paymentTimingDrilldown',
      kind,
      category,
      scopeForKey(scope),
    ],
  },

  leaseAbstraction: {
    all: [ROOT, 'leaseAbstraction'],
    /** One lease abstraction endpoint for a request scope (portfolios + filters). */
    dashboard: (endpoint, scope) => [
      ROOT,
      'leaseAbstraction',
      'dashboard',
      endpoint,
      scopeForKey(scope),
    ],
    /** Lease abstraction availability for the account. */
    availability: (accountId) => [ROOT, 'leaseAbstraction', 'availability', accountId ?? null],
    /** One lease abstract with its extracted fields. */
    abstract: (resultId) => [ROOT, 'leaseAbstraction', 'abstract', String(resultId)],
  },
}
